import type { RouteRecordRaw } from 'vue-router'
import { useSessionStore } from './stores/session'

/**
 * The signed-out screens, the screens an account email opens, and the organization picker a
 * sign-in lands on.
 *
 * **Every address below but `/signup`, `/forgot-password`, `/join/…` and `/organizations` is one
 * `base-auth` writes into its emails or redirects to**: `{base}/verify-email?token=…`,
 * `{base}/reset-password?token=…`, `{base}/confirm-email-change?token=…` (to the new address),
 * `{base}/revert-email-change?token=…` (to the previous one), `{base}/invitations/accept?token=…`,
 * and `/login`, where a provider round-trip comes back (`base_auth::http::LOGIN_PATH`). Moving any
 * of them breaks a link already sitting in somebody's inbox.
 *
 * **`/join/{organizationSlug}` is this product's, and is fixed too**: the `app` surface publishes it
 * as a membership request form's `form_url` and as the target of `/init`'s `membership_request`, and
 * an organization hands it out wherever it likes.
 */
export const sessionRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'session:login',
    component: () => import('./views/LoginView.vue'),
    meta: { title: 'Sign in', guestOnly: true },
  },
  {
    path: '/signup',
    name: 'session:signup',
    component: () => import('./views/SignupView.vue'),
    meta: { title: 'Sign up', guestOnly: true },
  },
  {
    path: '/forgot-password',
    name: 'session:forgot-password',
    component: () => import('./views/ForgotPasswordView.vue'),
    meta: { title: 'Forgot password', guestOnly: true },
  },
  {
    path: '/reset-password',
    name: 'session:reset-password',
    component: () => import('./views/ResetPasswordView.vue'),
    meta: { title: 'Reset password' },
  },
  {
    path: '/verify-email',
    name: 'session:verify-email',
    component: () => import('./views/VerifyEmailView.vue'),
    meta: { title: 'Verify email' },
  },
  {
    path: '/confirm-email-change',
    name: 'session:confirm-email-change',
    component: () => import('./views/ConfirmEmailChangeView.vue'),
    meta: { title: 'Confirm email change' },
  },
  {
    path: '/revert-email-change',
    name: 'session:revert-email-change',
    component: () => import('./views/RevertEmailChangeView.vue'),
    meta: { title: 'Undo email change' },
  },
  // Signed in or not: the invitee may already have an account, or be about to create it here.
  {
    path: '/invitations/accept',
    name: 'session:invitation-accept',
    component: () => import('./views/InvitationAcceptView.vue'),
    meta: { title: 'Invitation' },
  },
  // Signed in or not: anybody may ask to join an organization, through the address it hands out
  // or the sign-in screen's link when the platform points it there.
  {
    path: '/join/:organizationSlug',
    name: 'session:join',
    component: () => import('./views/JoinView.vue'),
    meta: { title: 'Request access' },
  },
  {
    path: '/organizations',
    name: 'organizations:list',
    component: () => import('./views/OrganizationPickerView.vue'),
    meta: { title: 'Organizations', requiresAuth: true },
    // One organization is not a choice: go straight in. `?pick` is the explicit way back to the
    // list for somebody who wants to see it anyway.
    beforeEnter: async (to) => {
      if (to.query.pick !== undefined) return true
      const session = useSessionStore()
      try {
        const access = await session.refreshAccess()
        const only = access.memberships.length === 1 ? access.memberships[0] : undefined
        if (only) return { name: 'home', params: { organizationId: only.org_id } }
      } catch {
        // The view renders the failure and offers to read it again.
      }
      return true
    },
  },
]
